/**
 * AlphaX Tuning Simulator - Audio Engine
 */

class RetroAudioSynth {
    constructor() {
        this.ctx = null;
        this.masterGain = null;
        this.filter = null;
        this.oscillators = [];
        this.isRunning = false;
        this.cylinders = 4;
        this.layout = "Inline";
        this.screechGain = null;
        this.turboGain = null;
        this.turboOsc = null;
        this.starterGain = null;
        this.starterNoise = null;
        this.starterLfo = null;
        this.starterMotorOsc = null;
        this.isDrySpindown = false;
        this.exhaustDepthFactor = 0;
    }

    init() {
        if (!this.ctx) {
            this.ctx = new (window.AudioContext || window.webkitAudioContext)();
            this.masterGain = this.ctx.createGain();
            this.masterGain.gain.value = 0;
            this.filter = this.ctx.createBiquadFilter();
            this.filter.type = 'lowpass';
            this.filter.frequency.value = 2000;
            this.masterGain.connect(this.filter);
            this.filter.connect(this.ctx.destination);
            this.setupOscillators();

            // Tire Screech Synth
            this.screechGain = this.ctx.createGain();
            this.screechGain.gain.value = 0;
            this.screechGain.connect(this.ctx.destination);
            
            const screechOsc = this.ctx.createOscillator();
            screechOsc.type = 'triangle'; 
            screechOsc.frequency.value = 1400; // High pitch squeal
            screechOsc.connect(this.screechGain);
            screechOsc.start();

            // Turbo Whine Synth
            this.turboGain = this.ctx.createGain();
            this.turboGain.gain.value = 0;
            this.turboGain.connect(this.ctx.destination);

            this.turboOsc = this.ctx.createOscillator();
            this.turboOsc.type = 'sine';
            this.turboOsc.frequency.value = 800;
            this.turboOsc.connect(this.turboGain);
            this.turboOsc.start();

            // Roots/Twin-Screw Supercharger Whine Synth
            // Sawtooth gives the harsh, harmonic-rich mechanical character distinct from turbo sine whine.
            // Frequency is driven by SC RPM (not boost), so it tracks engine RPM × pulley ratio.
            this.scGain = this.ctx.createGain();
            this.scGain.gain.value = 0;
            this.scGain.connect(this.ctx.destination);

            this.scOsc = this.ctx.createOscillator();
            this.scOsc.type = 'sawtooth';
            this.scOsc.frequency.value = 400;
            this.scOsc.connect(this.scGain);
            this.scOsc.start();
        }
    }

    setEngineProfile(cylinders, layout) {
        this.cylinders = cylinders;
        this.layout = layout;
        if (this.ctx) this.setupOscillators();
    }

    setExhaustDepth(factor) {
        this.exhaustDepthFactor = factor;
    }

    setupOscillators() {
        this.oscillators.forEach(o => { o.osc.stop(); o.osc.disconnect(); });
        this.oscillators = [];
        if (!this.ctx) return;

        let baseType = 'sawtooth';
        let subType = 'square';
        let baseVol = 0.5, subVol = 0, highVol = 0, highMult = 2;

        const cyl = this.cylinders;
        const lay = this.layout;

        if (lay === 'Rotary') {
            // Smooth triangle-sine for Wankel — high-pitch, silky
            baseType = 'triangle'; subType = 'sine';
            baseVol = 0.7; subVol = 0.4; highVol = 0.2; highMult = 2;
        } else if (cyl === 1) {
            // Single-cylinder: heavy fundamental thump, strong sub
            baseType = 'square'; subType = 'square';
            baseVol = 0.7; subVol = 0.5; highVol = 0.0; highMult = 2;
        } else if (cyl === 2 && lay === 'V') {
            // V-Twin: loping, characterful — strong sub for the lope
            baseType = 'square'; subType = 'sawtooth';
            baseVol = 0.6; subVol = 0.45; highVol = 0.05; highMult = 2;
        } else if (cyl === 2) {
            // Parallel Twin: punchier, less lope
            baseType = 'sawtooth'; subType = 'square';
            baseVol = 0.6; subVol = 0.25; highVol = 0.1; highMult = 2;
        } else if (cyl === 3) {
            // Inline-3: distinctive 3-cylinder offset hum
            baseType = 'sawtooth'; subType = 'square';
            baseVol = 0.6; subVol = 0.2; highVol = 0.15; highMult = 1.5;
        } else if (cyl === 4 && lay === 'Boxer') {
            // Boxer-4: Subaru burble — sawtooth sub gives the "potato" character
            baseType = 'sawtooth'; subType = 'sawtooth';
            baseVol = 0.55; subVol = 0.35; highVol = 0.1; highMult = 2;
        } else if (cyl === 4) {
            // Inline-4: standard crisp 4-cyl
            baseType = 'sawtooth'; subType = 'square';
            baseVol = 0.6; subVol = 0.2; highVol = 0.1; highMult = 2;
        } else if (cyl === 5) {
            // Inline-5: Audi warble — the offset firing gives a distinctive mid harmonic
            baseType = 'sawtooth'; subType = 'sawtooth';
            baseVol = 0.55; subVol = 0.3; highVol = 0.2; highMult = 1.4;
        } else if (cyl === 6 && lay === 'Boxer') {
            // Boxer-6: Porsche howl — higher harmonic presence
            baseType = 'sawtooth'; subType = 'sawtooth';
            baseVol = 0.5; subVol = 0.3; highVol = 0.25; highMult = 1.5;
        } else if (cyl === 6) {
            // Inline-6 / VR6 / V6: refined, smooth with good harmonic spread
            baseType = 'sawtooth'; subType = 'sawtooth';
            baseVol = 0.5; subVol = 0.3; highVol = 0.2; highMult = 1.5;
        } else if (cyl === 8 && lay === 'W') {
            // W8: dense thrum, strong square-wave body
            baseType = 'square'; subType = 'sawtooth';
            baseVol = 0.5; subVol = 0.5; highVol = 0.1; highMult = 2;
        } else if (cyl === 8) {
            // V8 / Inline-8 / Boxer-8: classic big-block rumble
            baseType = 'square'; subType = 'sawtooth';
            baseVol = 0.5; subVol = 0.6; highVol = 0.1; highMult = 2;
        } else if (cyl === 10) {
            // V10: high-rev screamer, extra high harmonic
            baseType = 'square'; subType = 'sawtooth';
            baseVol = 0.45; subVol = 0.55; highVol = 0.15; highMult = 1.5;
        } else if (cyl === 12 && lay === 'W') {
            // W12: turbine-smooth, very even
            baseType = 'square'; subType = 'square';
            baseVol = 0.45; subVol = 0.5; highVol = 0.1; highMult = 2;
        } else if (cyl === 12) {
            // V12: silky, refined — near-turbine smoothness
            baseType = 'square'; subType = 'square';
            baseVol = 0.45; subVol = 0.5; highVol = 0.12; highMult = 2;
        } else {
            // 16-cyl and beyond: turbine-like, extreme smoothness
            baseType = 'square'; subType = 'square';
            baseVol = 0.4; subVol = 0.5; highVol = 0.1; highMult = 2;
        }

        // Pre-set to a sub-audible frequency so there's no 440 Hz default burst
        // on first context resume. updateEngine() will move it to the real value
        // within the first frame.
        const initHz = 10;

        const mainOsc = this.ctx.createOscillator();
        mainOsc.type = baseType;
        mainOsc.frequency.value = initHz;
        const mainGain = this.ctx.createGain();
        mainGain.gain.value = baseVol;
        mainOsc.connect(mainGain);
        mainGain.connect(this.masterGain);
        mainOsc.start();
        this.oscillators.push({ osc: mainOsc, mult: 1 });

        if (subVol > 0) {
            const subOsc = this.ctx.createOscillator();
            subOsc.type = subType;
            subOsc.frequency.value = initHz * 0.5;
            const subGain = this.ctx.createGain();
            subGain.gain.value = subVol;
            subOsc.connect(subGain);
            subGain.connect(this.masterGain);
            subOsc.start();
            this.oscillators.push({ osc: subOsc, mult: 0.5 });
        }

        if (highVol > 0) {
            const highOsc = this.ctx.createOscillator();
            highOsc.type = 'sawtooth';
            highOsc.frequency.value = initHz * highMult;
            const highGain = this.ctx.createGain();
            highGain.gain.value = highVol;
            highOsc.connect(highGain);
            highGain.connect(this.masterGain);
            highOsc.start();
            this.oscillators.push({ osc: highOsc, mult: highMult });
        }
    }

    start() {
        this.init();
        this.isRunning = true;
        this.isDrySpindown = false;
        if (this.ctx.state === 'suspended') {
            // Store the chained promise so startStarter() can sequence after us
            this._startupPromise = this.ctx.resume().then(() => {
                this._startupPromise = null;
                this.masterGain.gain.setTargetAtTime(0.2, this.ctx.currentTime, 0.1);
            });
        } else {
            this._startupPromise = null;
            this.masterGain.gain.setTargetAtTime(0.2, this.ctx.currentTime, 0.1);
        }
    }

    stop() {
        if (!this.ctx) return;
        this.stopStarter();
        this.masterGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.1);
        this.setScreech(false);
        if (this.turboGain) this.turboGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.1);
        if (this.scGain) this.scGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.1);
        if (this.nitrousGain) this.nitrousGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.1);
        this.isRunning = false;
        this.isDrySpindown = false;
    }

    // Commanded engine stop — keep oscillators alive so they track the decaying RPM,
    // but mute combustion character: lower master gain and clamp filter to "dead cylinder" range.
    spindown() {
        if (!this.ctx || !this.isRunning) return;
        this.isDrySpindown = true;
        // Combustion energy gone — drop master gain from 0.2 to 0.10
        this.masterGain.gain.setTargetAtTime(0.10, this.ctx.currentTime, 0.15);
        // Kill boost/SC sounds immediately — no combustion, no exhaust driving them
        if (this.turboGain) this.turboGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.3);
        if (this.scGain) this.scGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.3);
        if (this.nitrousGain) this.nitrousGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.1);
        this.setScreech(false);
    }

    // startStarter() / stopStarter() — starter motor synth (removed, engine cranking sound used instead)
    // To restore: three-component synth layered on starterGain routed to ctx.destination.
    //   Component 1 — electric motor whine: sawtooth osc at 950 Hz → lowpass (3500 Hz, Q=0.5) → motorGain (0.18)
    //   Component 2 — mechanical body noise: 2s looping white noise → bandpass (500 Hz, Q=0.5) → noiseGain (0.35)
    //                                                                → bandpass (180 Hz, Q=0.7) → lowGain (0.45)
    //   Component 3 — compression chug: sine LFO at 10 Hz modulates noiseGain (±0.18) and lowGain (±0.22),
    //                 also wobbles motor osc frequency ±30 Hz for mechanical coupling feel.
    // masterGain was ducked to 0.1 on start (from 0.2) and restored on stop.
    // starterGain faded in to 0.1 with setValueAtTime(0)+setTargetAtTime over 100 ms.
    // ctx.resume() race on first load was fixed by chaining startStarter onto this._startupPromise.
    startStarter() {}
    stopStarter() {}

    updateEngine(rpm, loadPct, cylinders = 4, boostPsi = 0, scRpm = 0, scMaxRpm = 1, scType = '', scBypassThreshold = 15.0) {
        if (!this.isRunning || !this.ctx) return;
        if (!isFinite(rpm)) rpm = 0;
        if (!isFinite(loadPct)) loadPct = 0;
        if (!isFinite(boostPsi)) boostPsi = 0;
        if (!isFinite(scRpm)) scRpm = 0;
        // Rotary: each rotor fires 3× per rotor revolution; eccentric shaft = 3× rotor speed,
        // so firing frequency = rpm/60 × rotors × 1.5 (3 pulses / 2 strokes-per-revolution).
        // Piston: firing frequency = rpm/60 × cylinders / 2 (one power stroke per 720° per cyl).
        const baseHz = this.layout === 'Rotary'
            ? Math.max((rpm / 60.0) * (cylinders * 1.5), 10)
            : Math.max((rpm / 60.0) * (cylinders / 2.0), 10);
        this.oscillators.forEach(o => {
            o.osc.frequency.setTargetAtTime(baseHz * o.mult, this.ctx.currentTime, 0.05);
        });
        // Dry spindown: no combustion → clamp filter low for a deadened mechanical tone
        // Exhaust mods lower the filter base (deeper/darker tone) proportional to their peak torque gain
        const filterBase = 1000 - (this.exhaustDepthFactor * 300);
        const filterTarget = this.isDrySpindown ? 600 : filterBase + (loadPct * 3000);
        this.filter.frequency.setTargetAtTime(filterTarget, this.ctx.currentTime, 0.1);

        // Update Turbo / Centrifugal SC Sound (boost-driven, sine wave)
        // Centrifugal SC reuses this path since it sounds acoustically identical to a turbo.
        // Muted for roots/twin-screw — those have their own sawtooth synth below.
        if (this.turboOsc && this.turboGain) {
            const isRootsOrTwinScrew = (scType === 'roots' || scType === 'twin-screw');
            const turboFreq = 800 + (boostPsi * 200);
            const turboVol = isRootsOrTwinScrew ? 0 : Math.max(0, (boostPsi / 30) * 0.08);
            this.turboOsc.frequency.setTargetAtTime(turboFreq, this.ctx.currentTime, 0.1);
            this.turboGain.gain.setTargetAtTime(turboVol, this.ctx.currentTime, 0.1);
        }

        // Update Roots / Twin-Screw SC Whine (RPM-driven, sawtooth)
        // Pitch and volume track SC shaft speed directly — not boost.
        if (this.scOsc && this.scGain) {
            if ((scType === 'roots' || scType === 'twin-screw') && scMaxRpm > 0 && scRpm > 0) {
                // scRatioFreq is uncapped — blower keeps spinning faster past its rated max,
                // so pitch continues rising through redline.
                const scRatioFreq = scRpm / scMaxRpm;
                const scFreq = 200 + scRatioFreq * 1100;  // pitch tracks actual shaft speed, no ceiling
                
                // Volume logic: RPM-driven, but "significantly quieter" when bypass is open.
                // Bypass is open when TPS is below the threshold.
                const isBypassOpen = (loadPct * 100 < scBypassThreshold);
                const baseVol = Math.min(scRatioFreq, 1.0) * 0.05;
                const scVol = isBypassOpen ? baseVol * 0.10 : baseVol; // 90% quieter when open
                
                this.scOsc.frequency.setTargetAtTime(scFreq, this.ctx.currentTime, 0.05);
                this.scGain.gain.setTargetAtTime(scVol, this.ctx.currentTime, 0.05);
            } else {
                this.scGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.1);
            }
        }
    }

    triggerBov(boostPsi) {
        if (!this.isRunning || !this.ctx || boostPsi < 2) return;
        
        // Scale BOV intensity with boost pressure
        const intensity = Math.min(2.0, boostPsi / 25);
        const duration = 0.3 + (intensity * 0.4);
        
        const bufferSize = this.ctx.sampleRate * duration;
        const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
        const data = buffer.getChannelData(0);
        
        // White noise with exponential decay
        for (let i = 0; i < bufferSize; i++) {
            const pct = i / bufferSize;
            const decay = Math.pow(1 - pct, 2.5);
            data[i] = (Math.random() * 2 - 1) * decay * intensity;
        }

        const noiseSrc = this.ctx.createBufferSource();
        noiseSrc.buffer = buffer;

        const filter = this.ctx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = 2000 + (intensity * 3000);

        const gain = this.ctx.createGain();
        gain.gain.value = 0.1 + (intensity * 0.3);

        noiseSrc.connect(filter);
        filter.connect(gain);
        gain.connect(this.ctx.destination);
        
        noiseSrc.start();
    }

    triggerPop(intensity) {
        if (!this.isRunning || !this.ctx) return;
        const popDuration = Math.max(0.05, 0.1 + (intensity * 0.15));
        const bufferSize = Math.floor(this.ctx.sampleRate * popDuration);
        const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
            data[i] = (Math.random() * 2 - 1) * (1 - (i / bufferSize));
        }
        const noiseSrc = this.ctx.createBufferSource();
        noiseSrc.buffer = buffer;
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 500 + (intensity * 3000);
        const gain = this.ctx.createGain();
        gain.gain.value = 0.08 + (intensity * 0.20);
        noiseSrc.connect(filter);
        filter.connect(gain);
        gain.connect(this.ctx.destination);
        noiseSrc.start();
    }

    triggerFlamethrower() {
        if (!this.isRunning || !this.ctx) return;
        const now = this.ctx.currentTime;

        // NES-style chiptune flamethrower: two slightly detuned square waves
        // sweep downward in pitch like a retro fire burst
        const baseFreq = 80 + Math.random() * 120; // 80–200 Hz low whoosh
        const osc1 = this.ctx.createOscillator();
        osc1.type = 'square';
        osc1.frequency.setValueAtTime(baseFreq * 2.0, now);
        osc1.frequency.exponentialRampToValueAtTime(baseFreq * 0.5, now + 0.13);

        const osc2 = this.ctx.createOscillator();
        osc2.type = 'square';
        osc2.frequency.setValueAtTime(baseFreq * 2.03, now); // slight detune for NES chorus feel
        osc2.frequency.exponentialRampToValueAtTime(baseFreq * 0.52, now + 0.13);

        // Short noise burst layered in for exhaust texture
        const noiseLen = Math.floor(this.ctx.sampleRate * 0.08);
        const noiseBuf = this.ctx.createBuffer(1, noiseLen, this.ctx.sampleRate);
        const nd = noiseBuf.getChannelData(0);
        for (let i = 0; i < noiseLen; i++) nd[i] = (Math.random() * 2 - 1) * (1 - i / noiseLen);
        const noiseSrc = this.ctx.createBufferSource();
        noiseSrc.buffer = noiseBuf;
        const noiseFilter = this.ctx.createBiquadFilter();
        noiseFilter.type = 'bandpass';
        noiseFilter.frequency.value = 600;
        noiseFilter.Q.value = 1.5;

        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.20, now);
        gain.gain.setValueAtTime(0.20, now + 0.04);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);

        const noiseGain = this.ctx.createGain();
        noiseGain.gain.value = 0.06;

        osc1.connect(gain);
        osc2.connect(gain);
        noiseSrc.connect(noiseFilter);
        noiseFilter.connect(noiseGain);
        noiseGain.connect(gain);
        gain.connect(this.ctx.destination);

        osc1.start(now); osc1.stop(now + 0.20);
        osc2.start(now); osc2.stop(now + 0.20);
        noiseSrc.start(now);
    }

    triggerMetalBing() {
        if (!this.ctx) return;
        const osc = this.ctx.createOscillator();
        const osc2 = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        // Sharp high pitched ping
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(2500, this.ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(1500, this.ctx.currentTime + 0.2);

        // Dissonant overtone
        osc2.type = 'square';
        osc2.frequency.setValueAtTime(4200, this.ctx.currentTime);
        osc2.frequency.exponentialRampToValueAtTime(3000, this.ctx.currentTime + 0.2);

        gain.gain.setValueAtTime(1.0, this.ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, this.ctx.currentTime + 0.3);

        osc.connect(gain);
        osc2.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start();
        osc2.start();
        osc.stop(this.ctx.currentTime + 0.4);
        osc2.stop(this.ctx.currentTime + 0.4);
    }

    triggerMetalClack() {
        if (!this.ctx) return;
        const bufferSize = this.ctx.sampleRate * 0.08;
        const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
            data[i] = (Math.random() * 2 - 1) * Math.pow(1 - (i / bufferSize), 2);
        }
        const noiseSrc = this.ctx.createBufferSource();
        noiseSrc.buffer = buffer;
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 800; // Lower pitch for thicker clack
        filter.Q.value = 1.5; // Wider band
        const gain = this.ctx.createGain();
        gain.gain.value = 1.5; // Louder
        noiseSrc.connect(filter);
        filter.connect(gain);
        gain.connect(this.ctx.destination);
        noiseSrc.start();
    }

    triggerSoftKnock() {
        // "Pebble falling inside a tin can" — short, high-pitched, tinny
        if (!this.ctx) return;
        const now = this.ctx.currentTime;

        // Noise burst — the "pebble impact"
        const bufLen = Math.floor(this.ctx.sampleRate * 0.025);
        const buf = this.ctx.createBuffer(1, bufLen, this.ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < bufLen; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufLen, 1.5);
        const noiseSrc = this.ctx.createBufferSource();
        noiseSrc.buffer = buf;
        const noiseFilter = this.ctx.createBiquadFilter();
        noiseFilter.type = 'bandpass';
        noiseFilter.frequency.value = 4000;
        noiseFilter.Q.value = 2.5;
        const noiseGain = this.ctx.createGain();
        noiseGain.gain.value = 0.28;
        noiseSrc.connect(noiseFilter);
        noiseFilter.connect(noiseGain);
        noiseGain.connect(this.ctx.destination);
        noiseSrc.start(now);

        // Faint sine ping — the "tin resonance"
        const osc = this.ctx.createOscillator();
        const oscGain = this.ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(5200, now);
        osc.frequency.exponentialRampToValueAtTime(4400, now + 0.03);
        oscGain.gain.setValueAtTime(0.14, now);
        oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
        osc.connect(oscGain);
        oscGain.connect(this.ctx.destination);
        osc.start(now);
        osc.stop(now + 0.04);
    }

    triggerNormalKnock() {
        // "Tin can hitting the ground" — thud + metallic ring
        if (!this.ctx) return;
        const now = this.ctx.currentTime;

        // Low thud layer
        const thudLen = Math.floor(this.ctx.sampleRate * 0.08);
        const thudBuf = this.ctx.createBuffer(1, thudLen, this.ctx.sampleRate);
        const thudData = thudBuf.getChannelData(0);
        for (let i = 0; i < thudLen; i++) thudData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / thudLen, 1.2);
        const thudSrc = this.ctx.createBufferSource();
        thudSrc.buffer = thudBuf;
        const thudFilter = this.ctx.createBiquadFilter();
        thudFilter.type = 'bandpass';
        thudFilter.frequency.value = 400;
        thudFilter.Q.value = 1.8;
        const thudGain = this.ctx.createGain();
        thudGain.gain.value = 0.56;
        thudSrc.connect(thudFilter);
        thudFilter.connect(thudGain);
        thudGain.connect(this.ctx.destination);
        thudSrc.start(now);

        // Metallic ring layer
        const ringLen = Math.floor(this.ctx.sampleRate * 0.06);
        const ringBuf = this.ctx.createBuffer(1, ringLen, this.ctx.sampleRate);
        const ringData = ringBuf.getChannelData(0);
        for (let i = 0; i < ringLen; i++) ringData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / ringLen, 2);
        const ringSrc = this.ctx.createBufferSource();
        ringSrc.buffer = ringBuf;
        const ringFilter = this.ctx.createBiquadFilter();
        ringFilter.type = 'bandpass';
        ringFilter.frequency.value = 2100;
        ringFilter.Q.value = 3.0;
        const ringGain = this.ctx.createGain();
        ringGain.gain.value = 0.44;
        ringSrc.connect(ringFilter);
        ringFilter.connect(ringGain);
        ringGain.connect(this.ctx.destination);
        ringSrc.start(now);
    }

    triggerTorqueKnock() {
        // "Pebble hitting asphalt" — barely audible, flat, dry
        if (!this.ctx) return;
        const bufLen = Math.floor(this.ctx.sampleRate * 0.018);
        const buf = this.ctx.createBuffer(1, bufLen, this.ctx.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < bufLen; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufLen);
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        const filter = this.ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 6000;
        const gain = this.ctx.createGain();
        gain.gain.value = 0.05;
        src.connect(filter);
        filter.connect(gain);
        gain.connect(this.ctx.destination);
        src.start();
    }

    triggerLoudKnock() {
        // "Wrench dropped on a metal block" — heavy thud + sharp crack + ringing decay
        if (!this.ctx) return;
        const now = this.ctx.currentTime;

        // Heavy low thud
        const thudLen = Math.floor(this.ctx.sampleRate * 0.12);
        const thudBuf = this.ctx.createBuffer(1, thudLen, this.ctx.sampleRate);
        const thudData = thudBuf.getChannelData(0);
        for (let i = 0; i < thudLen; i++) thudData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / thudLen, 1.0);
        const thudSrc = this.ctx.createBufferSource();
        thudSrc.buffer = thudBuf;
        const thudFilter = this.ctx.createBiquadFilter();
        thudFilter.type = 'bandpass';
        thudFilter.frequency.value = 280;
        thudFilter.Q.value = 1.4;
        const thudGain = this.ctx.createGain();
        thudGain.gain.value = 1.1;
        thudSrc.connect(thudFilter);
        thudFilter.connect(thudGain);
        thudGain.connect(this.ctx.destination);
        thudSrc.start(now);

        // Sharp mid crack
        const crackLen = Math.floor(this.ctx.sampleRate * 0.04);
        const crackBuf = this.ctx.createBuffer(1, crackLen, this.ctx.sampleRate);
        const crackData = crackBuf.getChannelData(0);
        for (let i = 0; i < crackLen; i++) crackData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / crackLen, 2.5);
        const crackSrc = this.ctx.createBufferSource();
        crackSrc.buffer = crackBuf;
        const crackFilter = this.ctx.createBiquadFilter();
        crackFilter.type = 'bandpass';
        crackFilter.frequency.value = 1800;
        crackFilter.Q.value = 2.0;
        const crackGain = this.ctx.createGain();
        crackGain.gain.value = 0.75;
        crackSrc.connect(crackFilter);
        crackFilter.connect(crackGain);
        crackGain.connect(this.ctx.destination);
        crackSrc.start(now);

        // High metallic ring that decays
        const osc = this.ctx.createOscillator();
        const oscGain = this.ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(3200, now);
        osc.frequency.exponentialRampToValueAtTime(2400, now + 0.12);
        oscGain.gain.setValueAtTime(0.35, now);
        oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
        osc.connect(oscGain);
        oscGain.connect(this.ctx.destination);
        osc.start(now);
        osc.stop(now + 0.14);
    }

    triggerAmberBeep() {
        if (!this.ctx) return;
        const now = this.ctx.currentTime;
        const duration = 0.08;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.connect(gain); gain.connect(this.ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(660, now);
        gain.gain.setValueAtTime(0.85, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
        osc.start(now);
        osc.stop(now + duration);
        // Duck engine audio for the duration of the beep
        if (this.isRunning && this.masterGain) {
            this.masterGain.gain.cancelScheduledValues(now);
            this.masterGain.gain.setTargetAtTime(0.08, now, 0.01);
            this.masterGain.gain.setTargetAtTime(0.2, now + duration, 0.04);
        }
    }

    triggerGoBeep() {
        if (!this.ctx) return;
        const now = this.ctx.currentTime;
        const duration = 1.2;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.connect(gain); gain.connect(this.ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(1047, now);
        gain.gain.setValueAtTime(0.9, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
        osc.start(now);
        osc.stop(now + duration);
        // Duck engine audio for the duration of the go beep
        if (this.isRunning && this.masterGain) {
            this.masterGain.gain.cancelScheduledValues(now);
            this.masterGain.gain.setTargetAtTime(0.08, now, 0.01);
            this.masterGain.gain.setTargetAtTime(0.2, now + duration, 0.08);
        }
    }

    setScreech(active) {
        if (!this.ctx || !this.isRunning) return;
        this.screechGain.gain.setTargetAtTime(active ? 0.05 : 0, this.ctx.currentTime, 0.05);
    }

    triggerHoodClose() {
        const ctx = this.ctx || new (window.AudioContext || window.webkitAudioContext)();
        const play = () => {
            const now = ctx.currentTime;

            // Component 1: Low structural thud (the hood mass hitting the body)
            const thudBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.35), ctx.sampleRate);
            const thudData = thudBuf.getChannelData(0);
            for (let i = 0; i < thudData.length; i++)
                thudData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / thudData.length, 1.5);
            const thudSrc = ctx.createBufferSource();
            thudSrc.buffer = thudBuf;
            const thudBpf = ctx.createBiquadFilter();
            thudBpf.type = 'bandpass'; thudBpf.frequency.value = 90; thudBpf.Q.value = 0.8;
            const thudGain = ctx.createGain(); thudGain.gain.value = 1.75;
            thudSrc.connect(thudBpf); thudBpf.connect(thudGain); thudGain.connect(ctx.destination);
            thudSrc.start(now);

            // Component 2: Sheet metal resonance (panels ringing after impact)
            const ringBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.5), ctx.sampleRate);
            const ringData = ringBuf.getChannelData(0);
            for (let i = 0; i < ringData.length; i++)
                ringData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / ringData.length, 2.5);
            const ringSrc = ctx.createBufferSource();
            ringSrc.buffer = ringBuf;
            const ringBpf = ctx.createBiquadFilter();
            ringBpf.type = 'bandpass'; ringBpf.frequency.value = 380; ringBpf.Q.value = 2.0;
            const ringGain = ctx.createGain(); ringGain.gain.value = 0.4;
            ringSrc.connect(ringBpf); ringBpf.connect(ringGain); ringGain.connect(ctx.destination);
            ringSrc.start(now + 0.01);

            // Component 3: Latch click (hood latch engaging at end of travel)
            const clickBuf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 0.06), ctx.sampleRate);
            const clickData = clickBuf.getChannelData(0);
            for (let i = 0; i < clickData.length; i++)
                clickData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / clickData.length, 3);
            const clickSrc = ctx.createBufferSource();
            clickSrc.buffer = clickBuf;
            const clickHpf = ctx.createBiquadFilter();
            clickHpf.type = 'highpass'; clickHpf.frequency.value = 1200;
            const clickGain = ctx.createGain(); clickGain.gain.value = 0.6;
            clickSrc.connect(clickHpf); clickHpf.connect(clickGain); clickGain.connect(ctx.destination);
            clickSrc.start(now + 0.28);
        };
        if (ctx.state === 'suspended') ctx.resume().then(play); else play();
    }

    setNitrous(active) {
        if (!this.ctx) return;
        // Lazy-init: only create audio nodes on first activation (zero cost for non-nitrous users)
        if (!this.nitrousGain) {
            this.nitrousGain = this.ctx.createGain();
            this.nitrousGain.gain.value = 0;
            this.nitrousGain.connect(this.ctx.destination);

            // 2-second looping white noise buffer
            const bufSize = this.ctx.sampleRate * 2;
            const noiseBuf = this.ctx.createBuffer(1, bufSize, this.ctx.sampleRate);
            const data = noiseBuf.getChannelData(0);
            for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
            this.nitrousNoiseSrc = this.ctx.createBufferSource();
            this.nitrousNoiseSrc.buffer = noiseBuf;
            this.nitrousNoiseSrc.loop = true;

            // Bandpass filter: hissing rushing gas ~600 Hz
            this.nitrousBpf = this.ctx.createBiquadFilter();
            this.nitrousBpf.type = 'bandpass';
            this.nitrousBpf.frequency.value = 600;
            this.nitrousBpf.Q.value = 0.5;

            // Low rumble oscillator: ~40 Hz "woosh" undertone
            this.nitrousRumble = this.ctx.createOscillator();
            this.nitrousRumble.type = 'sine';
            this.nitrousRumble.frequency.value = 40;
            const rumbleGain = this.ctx.createGain();
            rumbleGain.gain.value = 0.3;

            this.nitrousNoiseSrc.connect(this.nitrousBpf);
            this.nitrousBpf.connect(this.nitrousGain);
            this.nitrousRumble.connect(rumbleGain);
            rumbleGain.connect(this.nitrousGain);
            this.nitrousNoiseSrc.start();
            this.nitrousRumble.start();
        }
        const now = this.ctx.currentTime;
        this.nitrousGain.gain.cancelScheduledValues(now);
        this.nitrousGain.gain.setTargetAtTime(active ? 0.12 : 0, now, active ? 0.005 : 0.05);
    }
}

const audio = new RetroAudioSynth();
